import React, { useState } from 'react';
import { Education } from '../../../types/profile';
import { EditableSection } from '../common/EditableSection';
import { FormInput } from '../common/FormInput';
import { FormSelect } from '../common/FormSelect';

interface EducationSectionProps {
  education: Education[];
  onUpdate: (education: Education[]) => void;
}

interface EducationFormProps {
  education: Education;
  onChange: (education: Education) => void;
  onRemove: () => void;
}

const EducationForm: React.FC<EducationFormProps> = ({ education, onChange, onRemove }) => {
  const handleFieldChange = (field: keyof Education) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    onChange({ ...education, [field]: e.target.value });
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 80; year--) {
      years.push(year.toString());
    }
    return years;
  };

  const yearOptions = generateYearOptions();
  const [startYear, endYear] = education.attendance ? education.attendance.split(' - ') : ['', ''];

  const handleYearChange = (type: 'start' | 'end') => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = e.target.value;
    const start = type === 'start' ? newYear : startYear;
    const end = type === 'end' ? newYear : endYear;
    const attendance = start && end ? `${start} - ${end}` : start || end;
    onChange({ ...education, attendance });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-start">
        <h4 className="text-lg font-medium text-gray-900">Education Entry</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
      <FormInput
        label="School/University"
        name="institution"
        value={education.institution}
        onChange={handleFieldChange('institution')}
        maxLength={100}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormSelect
          label="Start Year"
          name="start_year"
          value={startYear}
          onChange={handleYearChange('start')}
          options={yearOptions}
          placeholder="Select year"
        />
        <FormSelect
          label="End Year"
          name="end_year"
          value={endYear}
          onChange={handleYearChange('end')}
          options={yearOptions}
          placeholder="Select year"
        />
      </div>
      <FormInput
        label="Degree"
        name="degree_type"
        value={education.degree_type}
        onChange={handleFieldChange('degree_type')}
        maxLength={100}
        placeholder="e.g., Bachelor of Science"
      />
      <FormInput
        label="Field of Study"
        name="specialization"
        value={education.specialization}
        onChange={handleFieldChange('specialization')}
        maxLength={100}
        placeholder="e.g., Computer Science"
      />
      <FormInput
        label="GPA/Grade"
        name="gpa"
        value={education.gpa}
        onChange={handleFieldChange('gpa')}
        maxLength={20}
        placeholder="e.g., 3.8/4.0"
      />
    </div>
  );
};

export const EducationSection: React.FC<EducationSectionProps> = ({
  education,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editEducation, setEditEducation] = useState<Education[]>(education);

  const handleEdit = () => {
    setEditEducation(education);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditEducation(education);
    setIsEditing(false);
  };

  const handleSave = () => {
    onUpdate(editEducation);
    setIsEditing(false);
  };

  const handleAddEducation = () => {
    setEditEducation([
      ...editEducation,
      {
        institution: '',
        attendance: '',
        degree_type: '',
        specialization: '',
        gpa: ''
      }
    ]);
  };

  const handleUpdateEducation = (index: number) => (edu: Education) => {
    const updated = [...editEducation];
    updated[index] = edu;
    setEditEducation(updated);
  };

  const handleRemoveEducation = (index: number) => () => {
    setEditEducation(editEducation.filter((_, i) => i !== index));
  };

  return (
    <EditableSection
      title="Education"
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    >
      {isEditing ? (
        <div className="space-y-4">
          {editEducation.map((edu, index) => (
            <EducationForm
              key={index}
              education={edu}
              onChange={handleUpdateEducation(index)}
              onRemove={handleRemoveEducation(index)}
            />
          ))}
          <button
            type="button"
            onClick={handleAddEducation}
            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Education
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {education.length > 0 ? (
            education.map((edu, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                <h3 className="text-lg font-semibold text-gray-900">{edu.institution}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {edu.degree_type} {edu.specialization && `in ${edu.specialization}`}
                </p>
                <div className="flex justify-between items-center mt-1">
                  <p className="text-sm text-gray-500">{edu.attendance}</p>
                  {edu.gpa && <p className="text-sm text-gray-500">GPA: {edu.gpa}</p>}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">No education added yet.</p>
          )}
        </div>
      )}
    </EditableSection>
  );
};